<template>
  <div ref="root" v-show="isOpen" class="sheet-portal">
    <div class="sheet-portal__backdrop" :class="{ 'is-in': inView }" @click="close"></div>
    <div class="sheet-portal__panel" :class="{ 'is-in': inView }">
      <div class="sheet-portal__handle"></div>
      <h3 v-if="title" class="sheet-portal__title">{{ title }}</h3>
      <div class="sheet-portal__body">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
// Shared bottom sheet — the one consistent slide-up sheet treatment (matches the
// language / Wi-Fi / "review copied" sheets). Teleports to <body> so its fixed
// positioning ignores transformed / backdrop-filtered ancestors. v-model controls
// visibility.
import { lockNav, unlockNav } from "@/utils/navLock";

export default {
  name: "VBottomSheet",
  model: { prop: "value", event: "input" },
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: "" },
  },
  data() {
    return { isOpen: this.value, inView: false };
  },
  watch: {
    value(v) {
      v ? this.reveal() : this.dismiss();
    },
  },
  mounted() {
    if (this.$refs.root) document.body.appendChild(this.$refs.root);
    if (this.value) this.reveal();
  },
  beforeDestroy() {
    if (this.isOpen) unlockNav();
    if (this.$refs.root && this.$refs.root.parentNode) {
      this.$refs.root.parentNode.removeChild(this.$refs.root);
    }
  },
  methods: {
    reveal() {
      this.isOpen = true;
      lockNav();
      this.$nextTick(() => {
        window.requestAnimationFrame(() => { this.inView = true; });
      });
    },
    dismiss() {
      this.inView = false;
      unlockNav();
      setTimeout(() => { this.isOpen = false; }, 280);
    },
    close() {
      this.$emit("input", false);
      this.dismiss();
    },
  },
};
</script>

<style lang="scss" scoped>
.sheet-portal {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

.sheet-portal__backdrop {
  position: absolute;
  inset: 0;
  background: var(--overlay-scrim);
  opacity: 0;
  transition: opacity 280ms var(--ease-standard);

  &.is-in { opacity: 1; }
}

.sheet-portal__panel {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  max-height: 86%;
  display: flex;
  flex-direction: column;

  padding: 12px 20px calc(24px + var(--safe-bottom, 0px));

  background: var(--surface-card);
  border-radius: var(--radius-sheet) var(--radius-sheet) 0 0;
  box-shadow: var(--shadow-xl);

  transform: translateY(100%);
  transition: transform 320ms var(--ease-out);

  &.is-in { transform: translateY(0); }
}

.sheet-portal__handle {
  flex-shrink: 0;
  width: 42px;
  height: 5px;
  margin: 0 auto 14px;
  border-radius: var(--radius-pill);
  background: var(--border-default);
}

.sheet-portal__title {
  flex-shrink: 0;
  margin: 0 0 14px;
  font-size: var(--text-xl);
  font-weight: var(--weight-black);
  color: var(--text-primary);
}

.sheet-portal__body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
